import affirm from '../core/Affirm'
import algo from '../core/Algo'
import { ArgumentKeyType, IFlow, INode, INodeInput, NodeType, OutputKeyType } from '../definitions/Nodes'
import { ExecutionEnvironmentMode } from '../definitions/NodesExecution'
import { NodeOutputMap } from '../definitions/NodeTypes'
import NodeManager from './NodeManager'
import { NODE_UTILS } from './NodeUtils'

class ValidationManagerClass {
    validate = (flow: IFlow, mode?: ExecutionEnvironmentMode): { errors: string[] } => {
        const { errors: flowErr } = this.validateFlow(flow)
        affirm(flowErr.length === 0, `Flow validation failed:\n${flowErr.join(';\n')}`)

        const { errors: nodesErr } = this.validateNodes(flow.nodes)
        affirm(mode === 'compile' || flowErr.length === 0, `Nodes validation failed:\n${nodesErr.join(';\n')}`)

        return { errors: [...flowErr, ...nodesErr] }
    }

    validateFlow = (flow: IFlow): { errors: string[], nodesId: string[] } => {
        affirm(flow, 'Not flow was provided.')
        affirm(flow.nodes, 'The provided flow has null nodes')
        affirm(flow.nodes.every(algo.hasVal), 'Invalid nodes found inside flow')

        const errors: string[] = []
        const nodesId: string[] = []

        const { nodes } = flow

        if (flow.type === 'sub-flow') {
            if (!(nodes.filter(x => x.type === 'subFlow_start').length === 1)) {
                errors.push(`Found more than one node of type 'subFlow_start': only one start node is allowed in each flow.`)
                nodesId.push(null)
            }
            if (!(nodes.filter(x => x.type === 'subFlow_end').length === 1)) {
                errors.push(`Found more than one node of type 'subFlow_end': only one end node is allowed in each flow.`)
                nodesId.push(null)
            }
        } else {
            if (!(nodes.filter(x => x.type === 'start').length === 1)) {
                errors.push(`Found more than one node of type 'start': only one start node is allowed in each flow.`)
                nodesId.push(null)
            }
        }

        if (nodes.filter(x => x.uuid.includes('.')).length > 0) {
            errors.push(`Invalid token "." found in node id on node(s) "${nodes.filter(x => x.uuid.includes('.')).map(x => x.name).join(', ')}"`)
            nodesId.push(null)
        }

        const dupes = algo.duplicates(nodes, 'uuid')
        if (dupes.length > 0) {
            errors.push(`Duplicate nodes found in flow: ${dupes.length} (${dupes.map(x => x.uuid).join(', ')})`)
            nodesId.push(null)
        }

        return { errors: errors, nodesId: nodesId }
    }

    validateNodes = (nodes: INode<NodeType>[]): { errors: string[], nodesId: string[] } => {
        const errors: string[] = []
        const nodesId: string[] = []

        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i]

            // do the single node validation
            const e = this.validateNode(node)
            if (e && e.length > 0) {
                errors.push(NODE_UTILS.validationError(node, e).message)
                nodesId.push(node.uuid)
            }

            for (let j = 0; j < node.inputs.length; j++) {
                const input = node.inputs[j]

                // check that input nodes actually exist
                const inputNode = nodes.find(x => x.uuid === input.uuid)
                if (!inputNode) {
                    errors.push(`A node is asking for an input that doesn't exist: node "${node.name}(${node.uuid})" missing input(s) "${input.uuid}" (argument: "${input.argumentKey}" - output: "${input.outputKey}")`)
                    nodesId.push(node.uuid)
                } else {
                    // check that argument and output keys match and are valid
                    const inputDescription = NodeManager.getDescription(inputNode.type)
                    if (!inputDescription.outputs.some(x => x.key === input.outputKey)) {
                        errors.push(`The node ${node.name}(${node.uuid}) is requesting the input "${input.outputKey}" from ${inputNode.name}(${inputNode.uuid}), but this node doesn't return that output`)
                        nodesId.push(node.uuid)
                    }
                }
            }
        }
        return { errors, nodesId }
    }

    validateNode = <T extends NodeType>(node: INode<T>): string[] => {
        affirm(node, 'Invalid node passed to validate node')

        const description = NodeManager.getDescription(node.type)
        affirm(description, `No descriptor found for node "${node.type}"`)

        const errors: string[] = []
        for (let i = 0; i < description.acceptedInputs.length; i++) {
            const accepted = description.acceptedInputs[i]
            switch (accepted.count) {
                case 'one': {
                    if (!(node.inputs.filter(x => accepted.argumentKey === x.argumentKey).length === 1))
                        errors.push(`Expecting one and only one input for parameter "${String(accepted.argumentKey)}"`)
                    break
                }
                case 'one-or-more': {
                    if (!node.inputs.some(x => accepted.argumentKey === x.argumentKey))
                        errors.push(`Required input "${String(accepted.argumentKey)}" was not found`)
                    break
                }
                case 'zero-or-one': {
                    if (node.inputs.filter(x => accepted.argumentKey === x.argumentKey).length > 1)
                        errors.push(`Expecting zero or one input for parameter "${String(accepted.argumentKey)}", but received more than one`)
                    break
                }
                case 'zero-or-more': {
                    break
                }
            }
        }

        for (let i = 0; i < description.acceptedData.length; i++) {
            const acceptedData = description.acceptedData[i]
            if (acceptedData.isRequired && !node.data[acceptedData.parameter]) {
                errors.push(`The field "${String(acceptedData.parameter)}" is required but it's empty`)
            }
        }

        switch (node.type) {
            case 'xfrm_code': {
                const acceptedData = description.acceptedData.find(x => x.parameter === 'source')
                if (!acceptedData) {
                    errors.push(`This does not need the "source" parameter`)
                    break
                }

                const defaultValue: string = acceptedData.defaultValue?.trim()
                if (!defaultValue) {
                    errors.push(`This node was expecting a default value but was not found`)
                    break
                }

                const source = (node.data[acceptedData.parameter] as string).trim()

                const start = defaultValue.slice(0, defaultValue.indexOf('\n'))
                const end = '}'

                if (!source.startsWith(start))
                    errors.push(`The source field does not begin with the prefix "${start}"`)

                if (!source.endsWith(end))
                    errors.push(`The source field does not end with the suffix "${end}"`)
                break
            }
            default: break
        }

        if (description.acceptedInputs.length === 0 && node.inputs.length > 0)
            errors.push(`Node type "${node.type}" should have no inputs, but found "${node.inputs.length}"`)

        if (node.inputs.some(x => x.uuid === node.uuid))
            errors.push(`Invalid connection, can't connect a node with itself: "${node.name}(${node.uuid})"`)

        return errors
    }

    validateNodeResult = <T extends NodeType>(node: INode<T>, result: NodeOutputMap[T]): string[] => {
        affirm(node, 'Invalid node passed to validate node result')
        affirm(result, 'Invalid result passed to validate node result')

        const errors: string[] = []
        switch (node.type) {
            case 'xfrm_sql': {
                const output = result as NodeOutputMap['xfrm_sql']
                if (!Array.isArray(output.items))
                    errors.push(`Invalid return type "${node.name}": excpecting an array but got an "${typeof output.items}"`)
                break
            }
            case 'xfrm_groupby': {
                const output = result as NodeOutputMap['xfrm_groupby']
                if (output.keys.length === 0)
                    errors.push(`Invalid output data "${node.name}": expecting at least one key`)
                if (output.result.length === 0)
                    errors.push(`Invalid output data "${node.name}": excpecting a non empty array`)
                break
            }
            default: break
        }

        return errors
    }

    validateNewConnection = <T extends NodeType>(source: INode<NodeType>, target: INode<NodeType>, outputKey: OutputKeyType<T> | string, argumentKey: ArgumentKeyType<T> | string, flow?: IFlow): string[] => {
        affirm(source, 'Invalid source parameter')
        affirm(target, 'Invalid target parameter')
        affirm(outputKey, 'Invalid output key parameter')
        affirm(argumentKey, 'Invalid argument key parameter')
        affirm(target.uuid !== source.uuid, `Invalid connection, can't connect a node with itself`)

        const sourceDescription = NodeManager.getDescription(source.type)
        const targetDescription = NodeManager.getDescription(target.type)
        affirm(sourceDescription, `No descriptor found for node "${source.type}"`)
        affirm(targetDescription, `No descriptor found for node "${target.type}"`)

        const errors: string[] = []

        if (!sourceDescription.outputs.some(x => x.key === outputKey))
            errors.push(`Unknown output "${String(outputKey)}" in node "${source.type}"`)

        if (!targetDescription.acceptedInputs.some(x => x.argumentKey === argumentKey))
            errors.push(`Unknown argument "${String(argumentKey)}" in node "${target.type}"`)

        if (!targetDescription.acceptedInputNodes.some(x => x.nodeType === source.type))
            errors.push(`Invalid connection between nodes: the node type "${target.type}" doesn't accept nodes of type "${source.type}"`)

        const inputDescription = targetDescription.acceptedInputs.find(x => x.argumentKey === argumentKey)
        switch (inputDescription.count) {
            case 'one':
            case 'zero-or-one':
                if (target.inputs.filter(x => x.argumentKey === argumentKey).length > 0)
                    errors.push(`Maximum number of connections for input argument "${String(argumentKey)}" exceeded.`)
                break
            case 'one-or-more':
            case 'zero-or-more': break
        }

        if (target.inputs.length > 0) {
            // When a connection from end fork
            if (target.type === 'end_fork') {
                const familyTrees = this.getFamilyTrees([source, target], flow, [], true)
                const matchedNodesList = this.getMatchedNodes('fork', familyTrees, flow)
                if (!this.isIntersected(matchedNodesList)) errors.push(`You are trying to connect an end-fork node that doesn't match its origin fork node`)
            } else {
                // When a connection from the fork node is created
                if (source.type === 'fork' || source.type !== 'end_fork') {
                    const familyTree = this.getFamilyTrees([null, target], flow)
                    const matchedNodesList = this.getMatchedNodes('fork', familyTree, flow)
                    for (const matchedNode of matchedNodesList) {
                        if (matchedNode.find(m => m.uuid === source.uuid)?.outputKey !== outputKey) {
                            errors.push(`You are trying to connect a fork node in a different fork branch`)
                            break
                        }
                    }
                } else {
                    // When a connection from a node in a branch of fork is created
                    const familyTrees = this.getFamilyTrees([source, target], flow)
                    const matchedNodesList = this.getMatchedNodes('fork', familyTrees, flow)
                    if (this.isIntersected(matchedNodesList)) errors.push(`You are trying to connect a node of a fork branch that doesn't correspond to its origin one`)
                }
            }
        }

        /* FOR DEBUG */
        // if (Helper.isDev()) {
        //     console.group('DEBUG')
        //     console.dir(source)
        //     console.dir(target)
        //     console.dir(outputKey)
        //     console.dir(argumentKey)
        //     console.dir(flow)
        //     console.groupEnd()
        // }

        // TODO: make it so some inputs are accepted only from specific node types or that some are refused (either negative or positive space)

        return errors
    }

    getTree = (node: INode<NodeType>, flow: IFlow, visitedInputs: INodeInput<NodeType>[] = [], stopAtFork: boolean = false): INodeInput<NodeType>[] => {
        const inputNodes = node.inputs
        for (const inputNode of inputNodes) {
            const foundNode = flow.nodes.find(n => n.uuid === inputNode.uuid)
            visitedInputs.push(inputNode)
            if (algo.hasVal(foundNode)) {
                if (stopAtFork && foundNode.type === 'fork') {
                    continue
                }
                this.getTree(foundNode, flow, visitedInputs, stopAtFork)
            }
        }
        return visitedInputs
    }

    getFamilyTrees = (nodes: [INode<NodeType>, INode<NodeType>], flow: IFlow, familyTrees: INodeInput<NodeType>[] = [], stopAtFork: boolean = false): INodeInput<NodeType>[] => {
        const key = (input: INodeInput) => `${input.uuid}_${input.outputKey}_${input.argumentKey ?? ''}`

        for (const node of nodes) {
            if (!node) continue
            const familyTree = this.getTree(node, flow, [], stopAtFork)
            for (const familyLeaf of familyTree) {
                if (!familyTrees.some(existing => key(existing) === key(familyLeaf))) {
                    familyTrees.push(familyLeaf)
                }
            }
        }

        return familyTrees
    }

    getMatchedNodes = (type: NodeType, familyTrees: INodeInput<NodeType>[], flow: IFlow, matchedNodesList: INodeInput<NodeType>[][] = []): INodeInput<NodeType>[][] => {
        const nodesOfType = flow.nodes.filter(n => n.type === type)
        for (const node of nodesOfType) {
            const matchedNode = familyTrees.filter(f => f.uuid === node.uuid)
            matchedNodesList.push(matchedNode)
        }
        return matchedNodesList
    }

    isIntersected = (matchedNodesList: INodeInput<NodeType>[][]): boolean => {
        let hasIntersection = false
        for (const matchedNodes of matchedNodesList) {
            if (matchedNodes.length > 1) {
                hasIntersection = true
                break
            }
        }
        return hasIntersection
    }
}

const ValidationManager = new ValidationManagerClass()
export default ValidationManager
